import React, { FC, MouseEvent, useEffect, useState, useCallback, HTMLProps, useRef } from 'react';
import classNames from 'classnames';
import useKeyPressEvent from 'react-use/lib/useKeyPressEvent';
import { Key } from 'ts-key-enum';

import Input from 'vibo-ui/Input';
import Tooltip from 'vibo-ui/Tooltip';
import Spinner from 'vibo-ui/Spinner';
import TimeOptions from './TimeOptions';
import Dropdown from 'vibo-ui/Dropdown';
import ClickAwayListener from 'vibo-ui/ClickAwayListener';
import Icon, { IconmoonFont } from 'vibo-ui/Icon';

import i18n from 'i18n';
import {
  TIME_SUGGESTIONS,
  checkTimeRegexValid,
  findClosestTime,
  parseIncorrectTime,
} from './timeHelper';

import { TimePickerProps } from './interfaces';

import useStyles from './style';

const TimePicker: FC<TimePickerProps & HTMLProps<HTMLInputElement>> = ({
  value,
  loading,
  disabled,
  withTooltip,
  wrapperClassName,
  tooltipClassName,
  autoFocus,
  onChangeTime,
  onChange,
  withUnderline = true,
  placeholder = i18n.t('addTime'),
  tooltipIcon = <Icon className="tooltipIcon" icon={IconmoonFont['clock-24']} />,
  ...rest
}) => {
  const classes = useStyles();

  const [focused, setFocused] = useState<boolean>(false);
  const [currentValue, setCurrentValue] = useState<string>('');
  const [activeTime, setActiveTime] = useState<string>('');
  const [tooltipVisible, setTooltipVisible] = useState<boolean>(false);

  const inputRef = useRef<Nullable<HTMLInputElement>>(null);

  const handleSelectPrevTime = useCallback(() => {
    if (focused) {
      const currentIndex = TIME_SUGGESTIONS.indexOf(activeTime);
      const nextIndex = (currentIndex - 1 + TIME_SUGGESTIONS.length) % TIME_SUGGESTIONS.length;

      setActiveTime(TIME_SUGGESTIONS[nextIndex]);
    }
  }, [focused, activeTime, currentValue]);

  const handleSelectNextTime = useCallback(() => {
    if (focused) {
      const currentIndex = TIME_SUGGESTIONS.indexOf(activeTime);
      const nextIndex = (currentIndex + 1) % TIME_SUGGESTIONS.length;

      setActiveTime(TIME_SUGGESTIONS[nextIndex]);
    }
  }, [focused, activeTime, currentValue]);

  const handleConfirmActiveTime = useCallback(() => {
    if (focused) {
      if (!!activeTime) {
        handleSetValidTime(activeTime);
      } else {
        handleSetValidTime();
      }

      handleBlur();
      setFocused(false);
    }
  }, [activeTime, currentValue, focused]);

  const handleBlur = useCallback(() => {
    setActiveTime('');
    setFocused(false);
    inputRef.current?.blur();
  }, []);

  useKeyPressEvent(Key.ArrowUp, handleSelectPrevTime);
  useKeyPressEvent(Key.ArrowDown, handleSelectNextTime);
  useKeyPressEvent('Tab', handleBlur);
  useKeyPressEvent('w', handleSelectPrevTime);
  useKeyPressEvent('s', handleSelectNextTime);

  const handleBlurPicker = useCallback(() => {
    if (focused) {
      value !== currentValue && handleSetValidTime();

      handleBlur();
    }
  }, [focused, currentValue]);

  const handleSelectInput = (e: MouseEvent<HTMLInputElement>) => {
    setFocused(true);

    if (!focused) {
      // @ts-ignore
      e.target.select();
    }
  };

  const handleSetValidTime = useCallback(
    (val?: string) => {
      const newTime = val || currentValue;
      const parsedTime = parseIncorrectTime(newTime).trim();

      if (!newTime || checkTimeRegexValid(parsedTime)) {
        setCurrentValue(parsedTime);
        onChangeTime?.(parsedTime);
      } else {
        setUpDefaultTime(true);
      }
    },
    [currentValue]
  );

  const handleSelectTime = useCallback((e: MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    const time = e.currentTarget.textContent;

    if (!!time) {
      setCurrentValue(time);
      onChangeTime?.(time);
      handleBlur();
    }
  }, []);

  const setUpDefaultTime = useCallback(
    (resetToEmpty?: boolean) => {
      const defaultValue = `${value || rest.defaultValue}`;

      if (checkTimeRegexValid(defaultValue)) {
        return setCurrentValue(defaultValue);
      }

      if (resetToEmpty) {
        setCurrentValue('');
      }
    },
    [value, rest.defaultValue]
  );

  useEffect(() => {
    setUpDefaultTime();
  }, [value]);

  useEffect(() => {
    if (autoFocus) {
      setFocused(true);
    }
  }, []);

  const loadingIndicator = loading ? <Spinner size={12} /> : null;

  const Picker = (
    <ClickAwayListener className={wrapperClassName} onClickAway={handleBlurPicker}>
      <Dropdown
        overlayClassName={classNames('timePickerOverlay', classes.timePickerOverlay)}
        toggleClassName={classNames('timePickerToggle', classes.timePickerToggle)}
        overlay={
          <TimeOptions
            activeTime={activeTime}
            suggestions={TIME_SUGGESTIONS}
            handleSelectTime={handleSelectTime}
          />
        }
        visible={focused && !disabled}
      >
        <div
          className={classNames('clearTimeWrapper', classes.clearTimeWrapper, {
            iconUnderlinePosition: withUnderline,
            focused,
          })}
        >
          <Input
            value={currentValue}
            onChange={e => {
              const relevant = findClosestTime(e.currentTarget.value) ?? '';

              setActiveTime(relevant);
              setCurrentValue(e.currentTarget.value);
              onChange?.(e);
            }}
            autoFocus={autoFocus}
            placeholder={activeTime || placeholder}
            onClick={handleSelectInput}
            tabIndex={-1}
            disabled={disabled}
            className={classNames(classes.viboTimeInput, rest.className, {
              empty: !currentValue,
            })}
            onFocus={() => setFocused(true)}
            withUnderline={withUnderline}
            onPressEnter={handleConfirmActiveTime}
            ref={inputRef}
            id={rest.id}
            isTime
          />
          {loading ? (
            loadingIndicator
          ) : disabled || (!value && !currentValue) ? null : (
            <Icon
              onClick={() => {
                setCurrentValue('');
                onChangeTime?.('');
              }}
              className="clearIcon"
              icon={IconmoonFont['closeCircleFilled-16']}
            />
          )}
        </div>
      </Dropdown>
    </ClickAwayListener>
  );

  // const gg = findClosestTime(currentValue);

  // console.log(gg);

  return (
    <>
      {withTooltip ? (
        <Tooltip
          onVisibleChange={(visible: boolean) => {
            setTooltipVisible(visible);
            setFocused(visible);
          }}
          type="empty"
          trigger={['click']}
          overlay={Picker}
        >
          <div className={classNames(classes.tooltipContent, tooltipClassName)}>
            {tooltipVisible ? tooltipIcon : currentValue || tooltipIcon}
            {loadingIndicator}
          </div>
        </Tooltip>
      ) : (
        Picker
      )}
    </>
  );
};

export default TimePicker;
